import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { readState } from "@/lib/query/read-state";
import { GrantForm } from "@/routes/_authenticated/_knowledge/policies/-components/grant-form";
import { GrantTable } from "@/routes/_authenticated/_knowledge/policies/-components/grant-table";
import type { GrantRow } from "@/routes/_authenticated/_knowledge/policies/-components/grants";
import { useBuildingGrants } from "@/routes/_authenticated/_knowledge/policies/-components/use-building-grants";
import { useGrant } from "@/routes/_authenticated/_knowledge/policies/-components/use-grant";

export const Route = createFileRoute("/_authenticated/_knowledge/policies/$policyId/access")({
  component: PolicyAccessPage,
});

const fail = (error: Error) => toast.error(errorWords(error));
type Removing = { row: GrantRow; scope: "policy" | "facility" };

/** Who may read this one policy, and who may change it — here and building-wide. */
function PolicyAccessPage() {
  const { policyId } = Route.useParams();
  const canManage = useCan("policy:manage");
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<Removing | null>(null);

  const policy = useQuery(orpc.knowledge.policies.get.queryOptions({ input: { policyId } }));
  const building = policy.data?.facilityId ?? "";

  const grants = useQuery({
    ...orpc.knowledge.policies.grants.queryOptions({ input: { policyId } }),
    enabled: canManage,
  });
  const { facilityGrants, peopleWithout } = useBuildingGrants(building, canManage);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.knowledge.policies.key() });

  const grant = useGrant();
  const revoke = useMutation(
    orpc.knowledge.policies.revokeGrant.mutationOptions({
      onSuccess: async () => {
        toast.success("Access removed");
        setRemoving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const revokeFacility = useMutation(
    orpc.knowledge.policies.revokeFacilityGrant.mutationOptions({
      onSuccess: async () => {
        toast.success("Access removed");
        setRemoving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const policyRows = grants.data ?? [];
  const facilityRows = facilityGrants.data ?? [];

  if (policy.isPending) return <Skeleton className="h-64 w-full" />;
  if (policy.isError || !policy.data) {
    return (
      <EmptyState
        title="This policy could not be opened"
        description="It may have been removed, or it belongs to a building you cannot see."
      />
    );
  }

  return (
    <>
      <PageHeader
        title={`Access to ${policy.data.title}`}
        meta="Access granted here is on top of what somebody's role already allows."
        actions={
          <Button asChild variant="outline">
            <Link to="/policies/$policyId" params={{ policyId }}>
              Back to the policy
            </Link>
          </Button>
        }
      />

      {!canManage ? (
        <EmptyState
          title="Access is managed by somebody else"
          description="You can read this policy, but not decide who else may."
        />
      ) : (
        <Tabs defaultValue="policy">
          <TabsList>
            <TabsTrigger value="policy">This policy</TabsTrigger>
            <TabsTrigger value="building">Every policy in the building</TabsTrigger>
          </TabsList>

          <TabsContent value="policy" className="space-y-4">
            <GrantForm
              idPrefix="policy-grant"
              people={peopleWithout(policyRows)}
              pending={grant.isPending}
              emptyLabel="Everybody assigned to this building already has access to this policy."
              onGrant={(who, level) => grant.mutate({ scope: { policyId }, who, level })}
            />
            {grants.isPending ? (
              <Skeleton className="h-48 w-full" />
            ) : readState(grants) === "failed" ? (
              <ReadFailed
                message="Could not load who has access to this policy."
                error={grants.error}
                retrying={grants.isFetching}
                onRetry={() => void grants.refetch()}
              />
            ) : (
              <GrantTable
                rows={policyRows}
                canManage={canManage}
                emptyTitle="Nobody has been named on this policy"
                emptyDescription="People whose role already covers policies are not listed here."
                onRemove={(row) => setRemoving({ row, scope: "policy" })}
              />
            )}
          </TabsContent>

          <TabsContent value="building" className="space-y-4">
            <GrantForm
              idPrefix="building-grant"
              people={peopleWithout(facilityRows)}
              pending={grant.isPending}
              emptyLabel="Everybody assigned to this building already has building-wide access."
              onGrant={(who, level) =>
                building !== "" && grant.mutate({ scope: { facilityId: building }, who, level })
              }
            />
            {facilityGrants.isPending ? (
              <Skeleton className="h-48 w-full" />
            ) : readState(facilityGrants) === "failed" ? (
              <ReadFailed
                message="Could not load who has building-wide access."
                error={facilityGrants.error}
                retrying={facilityGrants.isFetching}
                onRetry={() => void facilityGrants.refetch()}
              />
            ) : (
              <GrantTable
                rows={facilityRows}
                canManage={canManage}
                emptyTitle="Nobody has building-wide access"
                emptyDescription="Access here covers every policy in this building, not just this one."
                onRemove={(row) => setRemoving({ row, scope: "facility" })}
              />
            )}
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog
        open={removing !== null}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing?.scope === "facility"
                ? `${removing.row.name} loses access to every policy in this building.`
                : `${removing?.row.name} loses access to this policy.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (!removing) return;
                if (removing.scope === "facility") {
                  revokeFacility.mutate({ grantId: removing.row.id });
                  return;
                }
                revoke.mutate({ grantId: removing.row.id });
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
